import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Circle, XCircle, Mail, Phone, MessageCircle, Users2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { FOLLOWUPS } from "@/constants/testIds/crm";
import HighlightText from "@/components/data-management/HighlightText";
import SavedViewsBar from "@/components/data-management/SavedViewsBar";
import StatusCountCards from "@/components/data-management/StatusCountCards";
import PaginationBar from "@/components/data-management/PaginationBar";
import { normalizeListResponse } from "@/lib/listResponse";
import { getStatusLabel, getStatusToneClasses, isOverdueFollowup } from "@/lib/statusStyles";

const TYPE_ICON = { email: Mail, call: Phone, whatsapp: MessageCircle, meeting: Users2 };
const SORT_OPTIONS = [
    { value: "date", label: "Soonest due" },
    { value: "-date", label: "Latest due" },
    { value: "created_at", label: "Oldest created" },
    { value: "-created_at", label: "Newest created" },
    { value: "status", label: "Status A-Z" },
];

export default function Followups() {
    const [items, setItems] = useState([]);
    const [counts, setCounts] = useState({});
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [leads, setLeads] = useState({});
    const [users, setUsers] = useState([]);
    const [q, setQ] = useState("");
    const [filter, setFilter] = useState("all");
    const [type, setType] = useState("");
    const [assignedUser, setAssignedUser] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [sort, setSort] = useState("date");

    const load = useCallback(async () => {
        try {
            const params = new URLSearchParams();
            params.set("page", page);
            params.set("limit", 10);
            if (q) params.set("q", q);
            if (filter !== "all") params.set("status", filter);
            if (type) params.set("type", type);
            if (assignedUser) params.set("assigned_user", assignedUser);
            if (dateFrom) params.set("date_from", dateFrom);
            if (dateTo) params.set("date_to", dateTo);
            if (sort) params.set("sort", sort);
            const { data } = await api.get(`/followups?${params.toString()}`);
            const normalized = normalizeListResponse(data);
            setItems(normalized.items);
            setTotal(normalized.total);
            setCounts(normalized.counts);
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message || "Failed to load follow-ups");
            setItems([]);
            setCounts({});
            setTotal(0);
        }
    }, [assignedUser, dateFrom, dateTo, filter, page, q, sort, type]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        api.get("/leads?limit=500")
            .then((r) => {
                const map = {};
                (r.data.items || []).forEach((l) => { map[l.id] = l; });
                setLeads(map);
            })
            .catch((err) => {
                toast.error(err.response?.data?.detail || err.message || "Failed to load leads");
                setLeads({});
            });
        api.get("/users")
            .then((r) => setUsers(normalizeListResponse(r.data).items))
            .catch(() => setUsers([]));
    }, []);

    const complete = async (fu) => {
        try {
            await api.patch(`/followups/${fu.id}`, { status: "completed" });
            toast.success("Marked completed");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message || "Failed to mark completed");
        }
    };
    const miss = async (fu) => {
        try {
            await api.patch(`/followups/${fu.id}`, { status: "missed" });
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message || "Failed to mark missed");
        }
    };

    const filtered = items;
    const viewState = { q, filter, type, assignedUser, dateFrom, dateTo, sort };
    const applyState = (next) => {
        setQ(next.q || "");
        setFilter(next.filter || "all");
        setType(next.type || "");
        setAssignedUser(next.assignedUser || "");
        setDateFrom(next.dateFrom || "");
        setDateTo(next.dateTo || "");
        setSort(next.sort || "date");
        setPage(1);
    };
    const statusCards = [
        ["Pending", counts.pending || 0],
        ["Completed", counts.completed || 0],
        ["Missed", counts.missed || 0],
        ["Skipped", counts.skipped || 0],
        ["Overdue", counts.overdue || 0],
    ].map(([label, value]) => ({ label, value }));

    return (
        <div className="space-y-6" data-testid={FOLLOWUPS.root}>
            <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary">Cadence</div>
                <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">Follow-ups</h1>
                <p className="text-sm text-muted-foreground mt-2">Every planned touchpoint across your entire pipeline.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <input value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }} placeholder="Search notes, stage, subject…" className="md:col-span-2 h-10 px-3 rounded-md border border-input bg-card text-sm" />
                <select value={filter} onChange={(e) => { setPage(1); setFilter(e.target.value); }} className="h-10 px-3 rounded-md border border-input bg-card text-sm">
                    <option value="all">All statuses</option>
                    {["pending", "completed", "missed", "skipped", "overdue"].map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
                <select value={type} onChange={(e) => { setPage(1); setType(e.target.value); }} className="h-10 px-3 rounded-md border border-input bg-card text-sm">
                    <option value="">All types</option>
                    {Object.keys(TYPE_ICON).map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <select value={sort} onChange={(e) => { setPage(1); setSort(e.target.value); }} className="h-10 px-3 rounded-md border border-input bg-card text-sm">
                    {SORT_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
                <select value={assignedUser} onChange={(e) => { setPage(1); setAssignedUser(e.target.value); }} className="h-10 px-3 rounded-md border border-input bg-card text-sm md:col-span-4">
                    <option value="">All assigned users</option>
                    {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
                <input type="date" value={dateFrom} onChange={(e) => { setPage(1); setDateFrom(e.target.value); }} className="h-10 px-3 rounded-md border border-input bg-card text-sm" />
                <input type="date" value={dateTo} onChange={(e) => { setPage(1); setDateTo(e.target.value); }} className="h-10 px-3 rounded-md border border-input bg-card text-sm" />
            </div>

            <SavedViewsBar
                storageKey="crm.followups.views"
                state={viewState}
                applyState={applyState}
                defaultState={{ q: "", filter: "all", type: "", assignedUser: "", dateFrom: "", dateTo: "", sort: "date" }}
            />

            <StatusCountCards
                cards={[
                    { label: "Total Follow-ups", value: total, hint: "All matching records" },
                    ...statusCards,
                ]}
                className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3"
            />

            <div className="border border-border rounded-md bg-card overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-secondary/40 border-b border-border">
                        <tr className="text-left text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                            <th className="px-4 py-3">Date</th>
                            <th className="px-4 py-3">Type</th>
                            <th className="px-4 py-3">Lead</th>
                            <th className="px-4 py-3">Notes</th>
                            <th className="px-4 py-3">Status</th>
                            <th className="px-4 py-3 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-muted-foreground">No follow-ups.</td></tr>}
                        {filtered.map((fu) => {
                            const lead = leads[fu.lead_id];
                            const Icon = TYPE_ICON[fu.type] || Mail;
                            return (
                                <tr key={fu.id} data-testid={FOLLOWUPS.row} className={`border-b border-border last:border-0 hover:brightness-[0.99] transition-colors ${getStatusToneClasses(fu.status === "overdue" ? "overdue" : fu.status, { group: "status", variant: "row" })}`}>
                                    <td className="px-4 py-3 font-mono text-xs">{new Date(fu.date).toLocaleString()}</td>
                                    <td className="px-4 py-3">
                                        <span className="inline-flex items-center gap-1.5 text-xs">
                                            <Icon className="h-3.5 w-3.5" /> {fu.type}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3">{lead ? <span className="font-medium"><HighlightText text={lead.company} query={q} /></span> : <span className="text-muted-foreground italic">Unknown</span>}</td>
                                    <td className="px-4 py-3 text-muted-foreground max-w-md truncate"><HighlightText text={fu.notes || "—"} query={q} /></td>
                                    <td className="px-4 py-3">
                                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider border ${getStatusToneClasses(isOverdueFollowup(fu.status, fu.date) ? "overdue" : fu.status, { group: "status", variant: "badge" })}`}>
                                            {isOverdueFollowup(fu.status, fu.date) && <AlertTriangle className="h-3 w-3" />}
                                            {getStatusLabel(isOverdueFollowup(fu.status, fu.date) ? "overdue" : fu.status)}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        {fu.status === "pending" && (
                                            <div className="inline-flex items-center gap-1">
                                                <button data-testid={FOLLOWUPS.completeButton} onClick={() => complete(fu)} className="inline-flex items-center gap-1 h-7 px-2 rounded text-xs text-success hover:bg-success/10 transition-colors">
                                                    <CheckCircle2 className="h-3.5 w-3.5" /> Done
                                                </button>
                                                <button onClick={() => miss(fu)} className="inline-flex items-center gap-1 h-7 px-2 rounded text-xs text-destructive hover:bg-destructive/10 transition-colors">
                                                    <XCircle className="h-3.5 w-3.5" /> Miss
                                                </button>
                                            </div>
                                        )}
                                        {fu.status !== "pending" && <Circle className="h-3 w-3 inline text-muted-foreground" />}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                <PaginationBar total={total} page={page} limit={10} onPageChange={setPage} />
            </div>
        </div>
    );
}
